import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';

import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import type { BackendClient } from '../bridge/backend-client';

/*
 * WHAT THE ASSISTANT MAY SEE OF A CONTROLLER'S PROGRAM.
 *
 * CLIENT TOOLS, for the same reachability reason as dashboards and models: the
 * program lives behind this relay's api-gateway, which is LAN-only on most
 * boxes and unreachable from the AI worker. A browser on the factory floor
 * reaches it either way, and the calls ride THIS person's session — the
 * assistant reads exactly what the person in front of the machine may read,
 * through the same ProgramIndex procedures the Program tab uses, and cannot
 * reach past its operator.
 *
 * READ-ONLY, structurally. There is no program.write and there must never be
 * one from this surface: a PLC program is the machine, extraction is one-way
 * by design, and an assistant that could edit what it analyses would be
 * writing firmware through a chat box. The pair here is list-then-read,
 * mirroring how a person uses the Program tab.
 *
 * THE PLAN GATE IS NOT HERE. These tools are declared by the browser and
 * stripped at the AI worker's edge for an org whose plan lacks
 * `functional_analysis` — a client file is the wrong place to enforce a plan,
 * because anyone's devtools can edit it. What THIS file guarantees is only
 * reachability and read-onlyness.
 */

export interface ProgramToolDeps extends GatewayScope {
  backend(): BackendClient | undefined;
}

const noBackend: ToolOutcome = { ok: false, message: 'The backend is not ready yet — try again in a moment.' };

/** Source is unbounded and a prompt is not. Enough ST to reason about a block, with a clear line
 *  saying what was left off rather than a silent truncation the model reads as the whole block. */
const MAX_SOURCE_CHARS = 24_000;

export function registerProgramTools(deps: ProgramToolDeps): () => void {
  const blocks: ClientTool = {
    name: 'program.blocks',
    // ProgramIndex/BlockTree.
    permission: Permission.PROGRAM_VIEW,
    description:
      "List the blocks of a controller's extracted PLC program — organization blocks, function blocks, data blocks — with each one's language and size. " +
      'Use this first, to see what the program contains and to find the block to read. It returns no source.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controllerId: { type: 'string', description: 'The controller whose program to list, e.g. from the open controller page.' },
      },
      required: ['controllerId'],
      additionalProperties: false,
    },
    run: async (args: { gateway?: string; controllerId: string }) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;
      try {
        const skel = await backend.api.fetchProgramSkeleton(gw.value.id, args.controllerId);
        if (skel.blocks.length === 0) {
          return {
            ok: true,
            message:
              skel.status === 'ready'
                ? `The controller has an extracted program but it contains no blocks.`
                : `No program has been extracted from this controller yet (status: ${skel.status || 'not started'}).`,
            data: { status: skel.status, blocks: [] },
          };
        }
        const rows = skel.blocks.map((b) => ({
          blockId: b.blockId,
          name: b.name,
          type: b.blockType,
          number: b.blockNumber,
          language: b.language,
          bytes: b.stBytes,
        }));
        return {
          ok: true,
          message: `${rows.length} block${rows.length === 1 ? '' : 's'} in the program on "${gw.value.name}": ${rows
            .map((r) => `${r.name} (${r.type}${r.number ? r.number : ''}, ${r.language})`)
            .join(', ')}.`,
          data: { status: skel.status, programHash: skel.programHash, blocks: rows },
        };
      } catch (e) {
        return { ok: false, message: `Could not list the program: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  const read: ClientTool = {
    name: 'program.read',
    // reads block SOURCE, not just the tree.
    permission: Permission.PROGRAM_SOURCE_VIEW,
    description:
      "Read one block's source from a controller's extracted PLC program — the Structured Text a person would see in the Program tab. " +
      'Use program.blocks first to find the blockId. Use this to explain what the logic does, trace why an output behaves as it does, or ground an analysis in the actual code.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controllerId: { type: 'string', description: 'The controller the block belongs to.' },
        blockId: { type: 'string', description: 'The block to read, as returned by program.blocks.' },
      },
      required: ['controllerId', 'blockId'],
      additionalProperties: false,
    },
    run: async (args: { gateway?: string; controllerId: string; blockId: string }) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;
      try {
        const src = await backend.api.fetchBlockSource(gw.value.id, args.controllerId, args.blockId);
        const truncated = src.text.length > MAX_SOURCE_CHARS;
        const text = truncated ? src.text.slice(0, MAX_SOURCE_CHARS) : src.text;
        return {
          ok: true,
          message: truncated
            ? `Block ${args.blockId}: first ${MAX_SOURCE_CHARS} of ${src.text.length} characters follow — the block is larger than fits here, and the tail was cut, not summarised.`
            : `Block ${args.blockId} (${src.bytes} bytes).`,
          data: { blockId: src.blockId, source: text, truncated, totalChars: src.text.length },
        };
      } catch (e) {
        return { ok: false, message: `Could not read the block: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  return registerClientTools(blocks, read);
}
